import React, { useState, useCallback, useMemo, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Modal,
  Button,
  TextField,
  Select,
  TextDescription,
  Checkbox,
  Typography,
} from "@ann/web-components";
import {
  ModalContent,
  FormField,
  ExpireContainer,
  ExpireValueInput,
  ExpireUnitSelect,
  NeverExpiresWrapper,
} from "./EditGeneralInformationModal.styled";
import { useMediaQueries } from "../../../../../../../utils/mediaQueries";
import { updateFact } from "../../../../../../../services";
import { useToast } from "../../../../../../../components/toast";

const STATUS_OPTIONS = [
  { value: "DRAFT", label: "Draft" },
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING", label: "Pending" },
  { value: "EXPIRED", label: "Expired" },
];

const EXPIRE_UNIT_OPTIONS = [
  { value: "DAYS", label: "Days" },
  { value: "WEEKS", label: "Weeks" },
  { value: "MONTHS", label: "Months" },
];

const EXPIRE_VALUE_OPTIONS = [
  { value: 30, label: "30" },
  { value: 60, label: "60" },
  { value: 90, label: "90" },
  { value: 180, label: "180" },
  { value: 365, label: "365" },
];

const EditGeneralInformationModal = ({ isOpen, onClose, factData, onSave }) => {
  const toast = useToast();
  const { factId } = useParams();
  const [formData, setFormData] = useState({
    title: "",
    fact: "",
    status: "DRAFT",
    expireValue: 90,
    expireUnit: "DAYS",
    neverExpires: false,
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { isMobile, isIpadAirPortrait, isIpadProPortrait } = useMediaQueries();

  const modalWidth = useMemo(() => {
    if (isMobile || isIpadAirPortrait || isIpadProPortrait) {
      return "95%";
    }
    return "500px";
  }, [isMobile, isIpadAirPortrait, isIpadProPortrait]);

  // Parse expiration data from factData
  useEffect(() => {
    if (factData && isOpen) {
      // Parse expiration from factData.expiration_policy or factData.expiration
      let expireValue = 90;
      let expireUnit = "DAYS";
      let neverExpires = false;

      if (factData.no_expire === true || factData.noExpire === true) {
        neverExpires = true;
      } else if (factData.expiration_policy) {
        expireValue = factData.expiration_policy.limit || 90;
        expireUnit = factData.expiration_policy.type || "DAYS";
      } else if (factData.expiration) {
        const expirationStr = factData.expiration.toLowerCase();
        if (expirationStr === "never" || expirationStr.includes("never")) {
          neverExpires = true;
        } else {
          const match = expirationStr.match(/(\d+)(d|w|m|day|week|month)/);
          if (match) {
            expireValue = parseInt(match[1]);
            const unit = match[2].toLowerCase();
            if (unit === "d" || unit === "day" || unit === "days") {
              expireUnit = "DAYS";
            } else if (unit === "w" || unit === "week" || unit === "weeks") {
              expireUnit = "WEEKS";
            } else if (unit === "m" || unit === "month" || unit === "months") {
              expireUnit = "MONTHS";
            }
          }
        }
      }

      setFormData({
        title: factData.title || factData.factName || "",
        fact: factData.description || factData.factDescription || "",
        status: factData.status ? factData.status.toUpperCase() : "DRAFT",
        expireValue,
        expireUnit,
        neverExpires,
      });
    }
  }, [factData, isOpen]);

  const handleFieldChange = useCallback(
    (field) => (value) => {
      setFormData((prev) => {
        // Handle expireValue as number when coming from Select
        const processedValue = field === "expireValue" ? Number(value) : value;
        return {
          ...prev,
          [field]: processedValue,
        };
      });
      if (errors[field]) {
        setErrors((prev) => ({
          ...prev,
          [field]: null,
        }));
      }
    },
    [errors]
  );

  const handleNeverExpiresChange = useCallback((checked) => {
    setFormData((prev) => ({
      ...prev,
      neverExpires: checked,
    }));
  }, []);

  const validateForm = useCallback(() => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Title is required";
    }

    if (!formData.fact.trim()) {
      newErrors.fact = "Fact is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;

    // Get fact_uuid from URL (factId param) or factData as fallback
    const factUuid = factId || factData?.fact_uuid || factData?.id || factData?.uuid;
    if (!factUuid) {
      toast.error({
        title: "Missing fact UUID",
        message: "fact_uuid is required to update a fact. Please ensure you're on a valid fact page.",
        canClose: true,
      });
      return;
    }

    const shouldIncludeExpirePolicy = !formData.neverExpires;
    const expireLimit = Number.parseInt(String(formData.expireValue), 10);

    if (shouldIncludeExpirePolicy) {
      if (!Number.isFinite(expireLimit) || expireLimit <= 0) {
        toast.error({
          title: "Invalid expiration",
          message: "Expire limit must be a positive number (or set 'Never expires').",
          canClose: true,
        });
        return;
      }
    }

    // Build payload - only include fields that have changed or are provided
    const payload = {
      fact_uuid: factUuid,
    };

    // Add optional fields only if they have values
    if (formData.title.trim()) {
      payload.title = formData.title.trim();
    }
    if (formData.fact.trim()) {
      payload.description = formData.fact.trim();
    }
    if (formData.status) {
      payload.status = formData.status;
    }

    // Handle expiration policy
    if (shouldIncludeExpirePolicy) {
      payload.expiration_policy = {
        limit: expireLimit,
        type: formData.expireUnit,
      };
    }
    payload.no_expire = Boolean(formData.neverExpires);

    // Validate that at least one field (besides fact_uuid) is provided
    const fieldsToUpdate = Object.keys(payload).filter(key => key !== "fact_uuid");
    if (fieldsToUpdate.length === 0) {
      toast.error({
        title: "No fields to update",
        message: "At least one updatable field must be provided.",
        canClose: true,
      });
      return;
    }

    try {
      setIsLoading(true);

      const response = await updateFact(payload);

      // Backend returns { statusCode, msgCode, body } even on HTTP 200
      if (response?.statusCode !== 200) {
        const code = response?.msgCode?.[0]?.code || "ERROR";
        const description =
          response?.msgCode?.[0]?.description || "Failed to update fact.";
        toast.error({ title: code, message: description, canClose: true });
        return;
      }

      toast.success({
        title: "Fact updated",
        message: response?.msgCode?.[0]?.description || "Fact updated successfully.",
        canClose: true,
      });

      if (onSave) {
        onSave(response?.body || formData);
      }

      // Close & reset
      setErrors({});
      onClose();
    } catch (error) {
      const apiErr = error?.response?.data;
      const code = apiErr?.msgCode?.[0]?.code || apiErr?.msgCode || "Error";
      const description = apiErr?.msgCode?.[0]?.description || apiErr?.error || "An error occurred while updating the fact.";

      toast.error({
        title: code,
        message: description,
        canClose: true,
      });
    } finally {
      setIsLoading(false);
    }
  }, [formData, validateForm, onSave, onClose, factData, toast]);

  const handleCancel = useCallback(() => {
    setErrors({});
    onClose();
  }, [onClose]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      size="medium"
      width={modalWidth}
    >
      <Modal.Header>
        <Modal.Header.Title>Edit General Information</Modal.Header.Title>
      </Modal.Header>
      <Modal.Body>
        <ModalContent>
          <Typography
            category="body"
            size="medium"
            weight="regular"
            color="body"
          >
            Update the general information for this Fact. All fields marked with * are
            required.
          </Typography>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Title *
            </Typography>
            <TextField
              value={formData.title}
              onChange={handleFieldChange("title")}
              placeholder="e.g., Customer Support Hours"
              size="small"
              state={errors.title ? "error" : "default"}
            />
            {errors.title && (
              <span
                style={{ color: "#d32f2f", fontSize: "12px", marginTop: "4px" }}
              >
                {errors.title}
              </span>
            )}
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Fact *
            </Typography>
            <TextDescription
              label=""
              value={formData.fact}
              onChange={handleFieldChange("fact")}
              placeholder="Enter the fact or information"
              height="120px"
              size="small"
            />
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Status *
            </Typography>
            <Select
              value={formData.status}
              onChange={handleFieldChange("status")}
              size="small"
            >
              <Select.Menu>
                {STATUS_OPTIONS.map((option) => (
                  <Select.Item
                    key={option.value}
                    value={option.value}
                    label={option.label}
                  >
                    {option.label}
                  </Select.Item>
                ))}
              </Select.Menu>
            </Select>
          </FormField>

          <FormField>
            <Typography category="label" size="small" weight="bold">
              Expire in
            </Typography>
            <ExpireContainer>
              <ExpireValueInput>
                <Select
                  value={formData.expireValue}
                  onChange={handleFieldChange("expireValue")}
                  size="small"
                  state={formData.neverExpires ? "disabled" : "default"}
                  placeholder="90"
                >
                  <Select.Menu>
                    {EXPIRE_VALUE_OPTIONS.map((option) => (
                      <Select.Item
                        key={option.value}
                        value={option.value}
                        label={option.label}
                      >
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Menu>
                </Select>
              </ExpireValueInput>
              <ExpireUnitSelect>
                <Select
                  value={formData.expireUnit}
                  onChange={handleFieldChange("expireUnit")}
                  size="small"
                  state={formData.neverExpires ? "disabled" : "default"}
                >
                  <Select.Menu>
                    {EXPIRE_UNIT_OPTIONS.map((option) => (
                      <Select.Item
                        key={option.value}
                        value={option.value}
                        label={option.label}
                      >
                        {option.label}
                      </Select.Item>
                    ))}
                  </Select.Menu>
                </Select>
              </ExpireUnitSelect>
              <NeverExpiresWrapper>
                <Checkbox
                  checked={formData.neverExpires}
                  onChange={handleNeverExpiresChange}
                  size="medium"
                />
                <Typography
                  category="body"
                  size="medium"
                  weight="regular"
                  color="body"
                >
                  Never expires
                </Typography>
              </NeverExpiresWrapper>
            </ExpireContainer>
          </FormField>
        </ModalContent>
      </Modal.Body>
      <Modal.Actions align="end">
        <Button
          label="Cancel"
          size="medium"
          type="secondary"
          onClick={handleCancel}
          disabled={isLoading}
        />
        <Button
          label="Save Changes"
          size="medium"
          type="primary"
          onClick={handleSave}
          disabled={isLoading}
        />
      </Modal.Actions>
    </Modal>
  );
};

export default EditGeneralInformationModal;

